import { useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Heading,
  Link,
  List,
  ListItem,
  PageLoader,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
  Text,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import { getInboxLinkAsync, type InboxSender } from '@/services/inbox-link';
import { useLoginFlow } from '../login-flow';
import { LoginAnalyticsEvent, trackLoginEvent } from '../utils/track-login-event';

type TwoFactorEmailLinkStepProps = {
  /** Magic-link token from `?token=`, present when arriving from the email link. */
  token: string | undefined;
};

// Hardcoded sender/support addresses, matching the Ember `2fa-email-link` template.
const SUPPORT_EMAIL_HREF = 'mailto:win@customer.io';
const SENDER_EMAIL = 'win@login.customer.io';
const TWO_FACTOR_DOCS_URL = 'https://docs.customer.io/accounts-and-workspaces/two-factor-auth/#2fa-via-email-links';

// The login email is sent from `win@login.customer.io` — sniper links must filter
// the inbox to this sender (not the signup verification sender the util defaults to).
const LOGIN_EMAIL_SENDER: InboxSender = { email: SENDER_EMAIL, domain: 'login.customer.io' };

export function TwoFactorEmailLinkStep({ token }: TwoFactorEmailLinkStepProps) {
  const { email, validateTwoFactorLink, resetToEmailStep } = useLoginFlow();
  const hasValidated = useRef(false);
  const [validationDone, setValidationDone] = useState(false);

  const hasToken = token != null && token !== '';

  // Resolve a provider-specific "sniper link" that deep-links the user straight to
  // the login email in their inbox. Best-effort: renders nothing if the provider
  // can't be resolved or the lookup fails.
  const { data: inboxLink } = useQuery({
    queryKey: ['login-inbox-link', email],
    queryFn: () => getInboxLinkAsync(email, LOGIN_EMAIL_SENDER),
    enabled: !isEmptyString(email),
    staleTime: Infinity,
  });

  const handleOpenInbox = (event: MouseEvent) => {
    event.preventDefault();
    if (inboxLink == null) {
      return;
    }
    trackLoginEvent(LoginAnalyticsEvent.InboxLinkClicked, {
      'MX Provider': inboxLink.provider,
      'Email Domain': inboxLink.email.split('@')[1] ?? '',
    });
    window.open(inboxLink.url, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    if (hasToken && !hasValidated.current) {
      hasValidated.current = true;
      void validateTwoFactorLink(token).then((ok) => {
        // On success the flow redirects away; on failure fall through to the
        // "check your email / Having Trouble?" content (matches Ember, which
        // only shows the loader while the validation task is running).
        if (!ok) {
          setValidationDone(true);
        }
      });
    }
  }, [hasToken, token, validateTwoFactorLink]);

  // While validating the token from the email link, show the loader (Ember:
  // `token && validateTwoFactorAuthLinkToken.isRunning`).
  if (hasToken && !validationDone) {
    return (
      <PanelSection>
        <PageLoader label="Logging you in…" />
      </PanelSection>
    );
  }

  return (
    <>
      <PanelHeader>
        <div>
          <PanelTitle level="1" mb="100">
            Welcome back!
          </PanelTitle>
          <Paragraph>
            Not this account?{' '}
            <Link onClick={resetToEmailStep} data-test-login-form-use-different-email>
              Use a different email
            </Link>
          </Paragraph>
        </div>
      </PanelHeader>
      <PanelSection>
        <Heading level="2" mb="100">
          Check your email
        </Heading>
        <Paragraph mb="200">
          {isEmptyString(email) ? (
            'We sent an email to you. '
          ) : (
            <>
              We sent an email to you at <Text color="active">{email}</Text>.{' '}
            </>
          )}
          Select the link to finish signing in.
        </Paragraph>

        {inboxLink != null ? (
          <Box mb="200">
            <Button
              variant="secondary"
              iconSrc={inboxLink.logoUrl}
              w="full"
              onClick={handleOpenInbox}
              data-test-login-inbox-link
            >
              Open in {inboxLink.label}
            </Button>
          </Box>
        ) : null}

        <Heading level="2" color="bold" mb="100">
          Having trouble?
        </Heading>
        <List mb="200">
          <ListItem>Do we have your email address correct?</ListItem>
          <ListItem>
            You'll need to select the link on the same browser and device you started the login process on.
          </ListItem>
          <ListItem>
            The email will be from{' '}
            <Link href={SUPPORT_EMAIL_HREF} variant="secondary" isExternal>
              {SENDER_EMAIL}
            </Link>{' '}
            with the subject line "Finish logging into Customer.io".
          </ListItem>
          <ListItem>Don't forget to check Spam or your Junk folder if it's not in your inbox.</ListItem>
        </List>

        <Paragraph>
          Still not working? We have a couple more suggestions in our{' '}
          <Link href={TWO_FACTOR_DOCS_URL} variant="secondary" isExternal>
            documentation
          </Link>{' '}
          or you can{' '}
          <Link href={SUPPORT_EMAIL_HREF} variant="secondary" isExternal>
            contact support
          </Link>
          .
        </Paragraph>
      </PanelSection>
    </>
  );
}
